import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import { form, FormField, maxLength, minLength, required } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ContactDetector, PropertyId, PropertyPresentationDto } from '@workspace/data-types';
import { AbstractSignalFormComponent, AlertAuraComponent, AuraColorType } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { PropertyDataCleaner } from '../../../utils/property-data.cleaner';

@Component({
  selector: 'etxe-property-presentation-form',
  imports: [CommonModule, FormField, MatFormFieldModule, MatInputModule, MatIconModule, AlertAuraComponent],
  templateUrl: './presentation-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyPresentationFormComponent extends AbstractSignalFormComponent<PropertyPresentationDto> {
  private readonly api = inject(ApiService);

  propertyId = input.required<PropertyId>();
  showTips = signal(false);
  auraColorType = AuraColorType;

  override model: WritableSignal<PropertyPresentationDto> = signal<PropertyPresentationDto>({
    title: '',
    description: '',
  });

  hasContactInTitle = computed(() => {
    const title = this.model().title || '';
    return ContactDetector.detect(title).hasContact;
  });

  hasContactInDescription = computed(() => {
    const desc = this.model().description || '';
    return ContactDetector.detect(desc).hasContact;
  });

  hasDetectedContact = computed(() => {
    return this.hasContactInTitle() || this.hasContactInDescription();
  });

  override formTree = form(this.model, (b) => {
    required(b.title);
    minLength(b.title, 5);
    maxLength(b.title, 50);

    required(b.description);
    minLength(b.description, 10);
    maxLength(b.description, 800);
  });

  toggleTips(): void {
    this.showTips.update((v) => !v);
  }

  protected override buildModelFromDto(dto: PropertyPresentationDto): PropertyPresentationDto {
    return {
      title: dto?.title ?? '',
      description: dto?.description ?? '',
    };
  }

  protected override buildDtoFromModel(value: PropertyPresentationDto): PropertyPresentationDto {
    const rawDto = {
      ...(this.dto() || ({} as PropertyPresentationDto)),
      title: value.title ?? '',
      description: value.description ?? '',
    } as PropertyPresentationDto;

    return PropertyDataCleaner.deepClean(rawDto) as PropertyPresentationDto;
  }
}
